import React from "react";
import { messages } from "../../../../localization/messages";
import { selectFilter } from "../../../../services/FilteringUtils";
import CommonUtil from "../../../../utils/CommonUtil";
import TableComponent from "../TableComponent";

/** @class Generic table with dialog that extends TableComponent.
 *
 *Supports:
 ** filtering and sorting from TableComponent
 ** in cell editing
 ** using dialog on adding new items
 ** deleting checked items
 */
class TableComponentWithDialog extends TableComponent {
  constructor(props) {
    super(props);

    this.props = props;

    this.state = {
      ...this.state,
      show: false,
      disabled: true,
      isNew: true,
      newEntity: undefined
    };

    this.inheritedData = {};
    this.allInheritedDataShouldBeDefined = false;
  }

  inheritedDataPropNames = [];

  getInheritedArraySafely = propName => {
    return this.getInheritedObject(propName, []);
  };
  getInheritedObjectSafely = propName => {
    return this.getInheritedObject(propName, {});
  };

  getInheritedObject = (propName, unexistingValue = undefined) => {
    var inheritedObject = CommonUtil.getDeep(this.inheritedData, propName);
    inheritedObject =
      inheritedObject === undefined ? unexistingValue : inheritedObject;
    return inheritedObject;
  };

  componentDidUpdate() {
    var inhertitedDataPropsChanged = false;
    var inhertitedDataPropsAllDefined = true;
    for (var i = 0; i < this.inheritedDataPropNames.length; i++) {
      const propName = this.inheritedDataPropNames[i];
      var newVal = undefined;
      if (
        !CommonUtil.objectsHasSameProperty(
          this.inheritedData,
          this.props,
          propName
        )
      ) {
        newVal = CommonUtil.getDeep(this.props, propName);
        CommonUtil.setDeepOnStringPath(this.inheritedData, propName, newVal);
        inhertitedDataPropsChanged = true;
      }
      if (newVal === undefined || newVal.length === 0) {
        inhertitedDataPropsAllDefined = false;
      }
    }

    if (inhertitedDataPropsChanged) {
      if (
        !this.allInheritedDataShouldBeDefined ||
        (this.allInheritedDataShouldBeDefined && inhertitedDataPropsAllDefined)
      ) {
        this.startLoading(this.state.pageInfo, this.sortInfo);
      }
    } else {
      super.componentDidUpdate();
    }
  }

  createTableIdFilters = (entities, comparator) => {
    let ids = entities.map(item => item.id);
    if (ids.length === 0) {
      ids = [-1];
    }
    return selectFilter(ids, comparator);
  };

  addPredefinedIdsFilter(pageInfo, predefined, comparator = "IN") {
    pageInfo = {
      ...pageInfo,
      filters: {
        idSpec: this.createTableIdFilters(predefined, comparator)
      }
    };
    return pageInfo;
  }

  componentWillMount() {
    this.setState({ newEntity: this.resetEntity() });
    super.componentWillMount();
  }

  resetComponent() {
    if (this.node && this.node.selectionContext) {
      this.node.selectionContext.selected = [];
    }

    super.resetComponent();
  }

  handleTableChange = (
    type,
    { page, sizePerPage, filters, sortField, sortOrder, data, cellEdit }
  ) => {
    // Handle cell editing
    if (type === "cellEdit") {
      const { rowId, dataField, newValue } = cellEdit;
      const result = data.find(element => element.id === rowId);
      result[dataField] = newValue;
      this.onCellSave(result);
    } else {
      this.onFilteringChange(type, {
        page,
        sizePerPage,
        filters,
        sortField,
        sortOrder
      });
    }
  };

  onDataSaveResponse = data => {
    this.handleClose();
    this.startLoading(this.state.pageInfo, this.sortInfo);
  };

  onDataDeleteResponse = data => {
    this.startLoading(this.state.pageInfo, this.sortInfo);
  };

  onDataReactivateResponse = data => {
    this.startLoading(this.state.pageInfo, this.sortInfo);
  };

  onCreateData = entity => {
    if (this.props.addToTable) {
      this.props.addToTable(entity);
      this.startLoading(this.state.pageInfo, this.sortInfo);
      this.handleClose();
    } else {
      throw new Error("You have to implement the method onCreateData!");
    }
  };

  onDeleteData = entity => {
    if (this.props.removeFromTable) {
      this.props.removeFromTable(entity);
      this.startLoading(this.state.pageInfo, this.sortInfo);
    } else {
      throw new Error("You have to implement the method onDeleteData!");
    }
  };

  onDataResponse = data => {};

  onCellSave = entity => {
    throw new Error("You have to implement the method onCellSave!");
  };

  updateData = entity => {
    throw new Error("You have to implement the method updateData!");
  };

  handleDialogChange = event => {
    throw new Error("You have to implement the method handleDialogChange!");
  };

  onSave = event => {
    if (this.state.isNew) {
      this.onCreateData(this.state.newEntity);
    } else {
      this.updateData(this.state.newEntity);
    }
  };

  getNonSelectableRows = () => {
    return [];
  };

  createSelectRow = () => {
    var selectRow = {
      mode: "checkbox",
      selectColumnStyle: {
        textAlign: "center"
      },
      nonSelectable: this.getNonSelectableRows()
    };

    if (this.columnsHaveFilter()) {
      selectRow.selectionHeaderRenderer = () => (
        <th class="sortable column-header-filtered">
          <div class="table-filter">
            <label class="filter-label">
              <input type="checkbox" />
            </label>
          </div>
        </th>
      );
    } else {
      selectRow.headerColumnStyle = {
        textAlign: "center"
      };
    }
    return selectRow;
  };

  columnsHaveFilter = () => {
    var haveFilter = false;
    this.columns.forEach(column => {
      if (column.hasOwnProperty("filter")) {
        haveFilter = true;
      }
    });
    return haveFilter;
  };

  handleShow = () => {
    this.setState({
      show: true,
      disabled: true,
      isNew: true,
      newEntity: this.resetEntity()
    });
  };

  handleEntered = () => {
    this.focusInput();
  };

  resetEntity = () => {
    return undefined;
  };

  handleClose = () => {
    this.setState({
      show: false
    });
  };

  setNode = n => {
    this.node = n;
  };

  onRowClick = (_, row) => {
    const selfUrl = row._links.self.href;
    this.loadSelectedData(selfUrl);
  };

  loadSelectedData = url => {
    throw new Error("Implment loading with self link");
  };

  onSelectedEntityLoad = data => {
    this.setState({
      newEntity: { ...data },
      oldEntity: { ...data },
      show: true,
      disabled: false,
      isNew: false
    });
  };

  handleOnDelete = () => {
    const entities = this.node.selectionContext.selected.map(selectedId => {
      return this.state.content.find(element => element.id === selectedId);
    });
    if (entities.length > 0) {
      const confirmed = window.confirm(
        messages.delete_selected_confirm_message
      );
      if (confirmed) {
        if (
          entities.length === this.state.content.length &&
          this.state.pageInfo.number >= this.state.pageInfo.totalPages - 1
        ) {
          this.state.pageInfo.number--;
        }
        entities.forEach(entity => this.onDeleteData(entity));
        this.node.selectionContext.selected = [];
      }
    } else {
      alert(messages.nothingSelectedMessage);
    }
  };

  handleOnReactivate = entity => {
    const confirmed = window.confirm(messages.reactivate_confirm_message);
    if (confirmed) {
      this.onReactivateData(entity);
    }
  };

  renderProps() {
    return {
      ...super.renderProps(),
      selectRow: this.createSelectRow(),
      show: this.state.show,
      entity: this.state.newEntity,
      node: this.setNode,
      onRowClick: this.onRowClick,
      handleShow: this.handleShow,
      handleEntered: this.handleEntered,
      handleOnDelete: this.handleOnDelete,
      handleClose: this.handleClose,
      onSave: this.onSave,
      onActiveButtonChange: this.onActiveButtonChange,
      onChange: this.handleDialogChange
    };
  }
}

export default TableComponentWithDialog;
